import {
  SearchIcon,
  XIcon,
  Loader2,
  Ellipsis,
  PictureInPicture,
} from "lucide-react";
import { Input } from "../ui/input";
import { useEffect, useMemo, useCallback, useState, useRef } from "react";
import * as Lucide from "lucide-react";
import { useActiveCanvas } from "@/utils/utils";
import type { JsxElement } from "@/utils/jsxElementTypes";
import { useCanvasStore } from "@/store/zustand/canvasStore";
import { cn } from "@/lib/utils";
import { useDispatch } from "react-redux";
import LucideReactGallery from "./LucideReactGallery";
import { useAssetComponents } from "@/hooks/useAssetComponents";
import { STORYBOARD_TYPES } from "@/utils/constants";
import { setSelectedElementKey } from "@/store/iframeSlice";
import { TempoElement } from "@/utils/tempoElement";
import React from "react";
import type { SubCanvas } from "@/utils/types";
import {
  type Tab,
  useAssetsPanelStore,
} from "@/store/zustand/assetsPanelStore";
import { AssetItem } from "./AssetItem";
import { DetailsPanel } from "./DetailsPanel";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import useLayoutStore from "@/store/zustand/layoutStore";
import { useCanvasPos } from "@/store/zustand/canvasStore";
import { useInitializeStoryboard } from "@/hooks/storyboards/useInitializeStoryboards";
import { useCreateStoryboard } from "@/hooks/storyboards/useCreateStoryboard";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { BulkPreviewModal } from "./BulkPreviewModal";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useElementMetadata } from "../contexts/ElementMetadataContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Badge } from "../ui/badge";

interface AssetGridProps {
  components: JsxElement[];
  detailedAsset: JsxElement | null;
  setDetailedAsset: (asset: JsxElement | null) => void;
  handleAddToCanvas: (
    assetToAdd: JsxElement,
    variantToAdd?: JsxElement
  ) => void;
  getVariants?: (component: JsxElement) => JsxElement[];
  onItemClick?: (component: JsxElement) => void;
}

const AssetGrid = React.memo(
  ({
    components,
    detailedAsset,
    setDetailedAsset,
    handleAddToCanvas,
    getVariants = () => [],
    onItemClick,
  }: AssetGridProps) => {
    const handleItemClick = useCallback(
      (component: JsxElement) => {
        if (onItemClick) {
          onItemClick(component);
        } else {
          setDetailedAsset(
            detailedAsset?.key === component.key ? null : component
          );
        }
      },
      [onItemClick, detailedAsset?.key, setDetailedAsset]
    );

    const handleAddToCanvasWithVariant = useCallback(
      (component: JsxElement, variant?: JsxElement) => {
        handleAddToCanvas(component, variant);
      },
      [handleAddToCanvas]
    );

    return (
      <div className="grid grid-cols-[repeat(auto-fill,minmax(100px,1fr))] gap-2 p-4 overflow-y-scroll h-full auto-rows-[max-content] scrollbar-hide">
        {components.map((component) => {
          const variants = getVariants(component);
          const isSelectedForDetails = detailedAsset?.key === component.key;

          return (
            <Popover
              key={component.key}
              open={isSelectedForDetails}
              onOpenChange={(open) => !open && setDetailedAsset(null)}
            >
              <PopoverTrigger asChild>
                <div
                  className={cn(
                    "relative group cursor-pointer rounded-md border border-transparent transition-colors",
                    detailedAsset?.key === component.key
                      ? "bg-secondary-200 border-secondary-300"
                      : "hover:bg-secondary-100"
                  )}
                  onClick={() => handleItemClick(component)}
                >
                  <AssetItem
                    element={component}
                    variants={variants}
                    selected={isSelectedForDetails}
                    onSelect={
                      onItemClick ? () => onItemClick(component) : undefined
                    }
                  />
                </div>
              </PopoverTrigger>
              <PopoverContent
                side="right"
                align="start"
                className="p-0 border-none w-auto bg-transparent shadow-none"
              >
                {isSelectedForDetails && (
                  <DetailsPanel
                    component={component}
                    variants={variants}
                    onClose={() => setDetailedAsset(null)}
                    onAddToCanvas={(variant) =>
                      handleAddToCanvasWithVariant(component, variant)
                    }
                  />
                )}
              </PopoverContent>
            </Popover>
          );
        })}
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function to prevent unnecessary re-renders
    return (
      prevProps.components.length === nextProps.components.length &&
      prevProps.components.every(
        (comp, i) => comp.key === nextProps.components[i].key
      ) &&
      prevProps.detailedAsset?.key === nextProps.detailedAsset?.key
    );
  }
);

export interface AssetsPanelProps {}

interface TabButtonProps {
  tab: string;
  activeTab: string;
  setActiveTab: (tab: string) => void;
}

const TabButton = React.memo(
  ({ tab, activeTab, setActiveTab }: TabButtonProps) => (
    <button
      className={cn(
        "p-0 text-xs font-semibold transition-colors hover:text-text-primary",
        activeTab === tab ? "text-secondary-900" : "text-secondary-700"
      )}
      onClick={() => setActiveTab(tab)}
    >
      {tab}
    </button>
  )
);

interface SearchInputProps {
  searchVal: string;
  setSearchVal: (val: string) => void;
  activeTab: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClearSearch: () => void;
}

const SearchInput = React.memo(
  ({
    searchVal,
    activeTab,
    onSearchChange,
    onClearSearch,
  }: SearchInputProps) => (
    <div className="group w-full pr-0">
      <div className="relative">
        <Input
          placeholder={`Search ${activeTab}`}
          className="w-full pl-9 h-7 bg-secondary-200 text-xs text-secondary-n-50 placeholder:text-secondary-n-600 border-0 focus-within:border-primary-0 focus-within:border transition"
          value={searchVal}
          onChange={onSearchChange}
        />
        <SearchIcon
          className={
            "absolute top-1/2 left-3 -translate-y-1/2 w-4 h-4 text-text-muted group-focus-within:text-primary-0 transition"
          }
        />
        {searchVal && (
          <button
            type="button"
            className="absolute top-1/2 right-3 -translate-y-1/2 text-secondary-500 hover:text-white"
            onClick={onClearSearch}
          >
            <XIcon className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  )
);

export const AssetsPanel = () => {
  const [showBulkPreviewModal, setShowBulkPreviewModal] = useState(false);
  const {
    searchVal,
    setSearchVal,
    activeTab,
    setActiveTab,
    variantSelected,
    setVariantSelected,
    detailedAsset,
    setDetailedAsset,
  } = useAssetsPanelStore();

  const { ungeneratedCount } = useElementMetadata();

  const canvasComponentKey = useCanvasStore(
    (store) => store.canvasComponentKey
  );

  const canvas = useActiveCanvas();
  const dispatch = useDispatch<any>();
  const initializeStoryboard = useInitializeStoryboard();
  const createStoryboard = useCreateStoryboard();
  const { transformX, transformY } = useCanvasPos();
  const { leftExpanded, rightExpanded } = useLayoutStore((state) => ({
    leftExpanded: state.leftExpanded,
    rightExpanded: state.rightExpanded,
  }));

  const [allLucideIcons, setAllLucideIcons] = React.useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const allIcons = Array.from(Object.keys(Lucide)).filter(
      (iconName: string) =>
        iconName.endsWith("Icon") && iconName !== "createLucideIcon"
    );
    setAllLucideIcons(allIcons);
  }, []);

  const filteredIcons = useMemo(() => {
    if (!searchVal) {
      return allLucideIcons;
    }
    return allLucideIcons.filter((iconName: string) =>
      iconName.toLowerCase().includes(searchVal.toLowerCase())
    );
  }, [searchVal, allLucideIcons]);

  const { componentKeyToComponent, componentKeyToStories } =
    useAssetComponents();

  useEffect(() => {
    if (Object.keys(componentKeyToComponent).length > 0) {
      setIsLoading(false);
    }
  }, [componentKeyToComponent]);

  const { componentsFiltered, storyComponentsFiltered } = useMemo(() => {
    const componentsWithStory: JsxElement[] = [];
    const componentsWithoutStory: JsxElement[] = [];
    const directSearchMatchComponentKeys = new Set();

    Object.keys(componentKeyToComponent).forEach((componentKey) => {
      const component = componentKeyToComponent[componentKey];
      component.key = componentKey;

      if (componentKeyToStories[componentKey]) {
        componentsWithStory.push(component);
      } else {
        componentsWithoutStory.push(component);
      }
    });

    if (!searchVal) {
      return {
        componentsFiltered: componentsWithoutStory,
        storyComponentsFiltered: componentsWithStory,
        directSearchMatchComponentKeys,
      };
    }

    const storyComponentsFiltered = componentsWithStory.filter((component) => {
      if (
        component.componentName?.toLowerCase().includes(searchVal.toLowerCase())
      ) {
        directSearchMatchComponentKeys.add(component.key);
        return true;
      }

      const stories = componentKeyToStories[component.key];
      return stories.some((story) =>
        // @ts-expect-error
        story.componentName.toLowerCase().includes(searchVal.toLowerCase())
      );
    });

    return {
      componentsFiltered: componentsWithoutStory.filter((component) =>
        component.componentName?.toLowerCase().includes(searchVal.toLowerCase())
      ),
      storyComponentsFiltered,
      directSearchMatchComponentKeys,
    };
  }, [searchVal, componentKeyToComponent, componentKeyToStories]);

  const tabs = useMemo(() => ["Storybook", "Components", "Icons"], []);

  const handleAddToCanvas = useCallback(
    async (assetToAdd: JsxElement, variantToAdd?: JsxElement) => {
      const itemToAdd = variantToAdd || assetToAdd;
      setDetailedAsset(null);

      if (!canvas) {
        console.error("Active canvas not found.");
        return;
      }

      if (!itemToAdd.codebaseId) {
        console.error(
          "Component to add does not have a codebaseId:",
          itemToAdd
        );
        return;
      }

      // try to approximate the canvas center
      const currentTransformX = transformX || 0;
      const currentTransformY = transformY || 0;

      const leftPanelWidth = leftExpanded ? 300 : 0;
      const rightPanelWidth = rightExpanded ? 300 : 0;
      const headerHeight = 50;

      const estimatedViewportWidth =
        window.innerWidth - leftPanelWidth - rightPanelWidth;
      const estimatedViewportHeight = window.innerHeight - headerHeight;

      // default fixed size to be smaller than usual storyboards
      const sbWidth = 400;
      const sbHeight = 300;

      const centerX_viewport = estimatedViewportWidth / 2;
      const centerY_viewport = estimatedViewportHeight / 2;

      const centerX_canvas = centerX_viewport + currentTransformX;
      const centerY_canvas = centerY_viewport + currentTransformY;

      const finalX = centerX_canvas - sbWidth / 2;
      const finalY = centerY_canvas - sbHeight / 2;

      // useful debug
      // console.log("Storyboard Placement Calculation (Attempt 2: +transform):", {
      //     zoom,
      //     currentTransformX,
      //     currentTransformY,
      //     estimatedViewportWidth, // Using estimated width now
      //     estimatedViewportHeight, // Using estimated height now
      //     leftPanelWidth, // Log panel widths used
      //     rightPanelWidth, // Log panel widths used
      //     centerX_canvas,
      //     centerY_canvas,
      //     sbWidth,        // Storyboard width
      //     sbHeight,       // Use sbHeight here
      //     finalX,
      //     finalY
      // });
      //

      const newStoryboard = initializeStoryboard(
        finalX,
        finalY,
        sbWidth,
        sbHeight
      );

      const componentToAddArg = {
        componentId: itemToAdd.codebaseId,
      };

      try {
        await createStoryboard({
          storyboard: newStoryboard,
          componentToAdd: componentToAddArg,
          actionText: "Added Component from Panel",
        });
      } catch (error) {
        console.error("Failed to create storyboard:", error);
        toast.error("Failed to create storyboard:", error);
      }
    },
    [
      canvas,
      transformX,
      transformY,
      leftExpanded,
      rightExpanded,
      initializeStoryboard,
      createStoryboard,
      setDetailedAsset,
    ]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchVal(e.target.value);
    },
    [setSearchVal]
  );

  const handleClearSearch = useCallback(() => {
    setSearchVal("");
  }, [setSearchVal]);

  const handleTabChange = useCallback(
    (tab: Tab) => {
      setActiveTab(tab);
    },
    [setActiveTab]
  );

  useEffect(() => {
    if (
      !variantSelected ||
      !canvasComponentKey ||
      !canvas ||
      !canvas.storyboards ||
      (canvas as SubCanvas).component_id !== canvasComponentKey
    ) {
      return;
    }

    const selectVariant = async () => {
      const storyboardToSelect = canvas.storyboards.find(
        (storyboard) =>
          storyboard.type === STORYBOARD_TYPES.STORY &&
          storyboard.name === variantSelected.story.componentName
      );

      if (!storyboardToSelect) {
        return;
      }

      dispatch(
        setSelectedElementKey(TempoElement.forStoryboard(storyboardToSelect.id))
      );
      setVariantSelected(null);
    };

    selectVariant();
  }, [
    variantSelected,
    canvasComponentKey,
    canvas,
    dispatch,
    setVariantSelected,
  ]);

  return (
    <div className="w-full h-full flex flex-col bg-secondary-0">
      <div className="flex font-medium text-xs p-2.5 gap-2.5">
        {tabs.map((tab) => (
          <TabButton
            key={tab}
            tab={tab}
            activeTab={activeTab}
            setActiveTab={handleTabChange}
          />
        ))}
      </div>

      <div className="flex items-center justify-between border-y border-border-primary p-4 gap-2">
        <SearchInput
          searchVal={searchVal}
          setSearchVal={setSearchVal}
          activeTab={activeTab}
          onSearchChange={handleSearchChange}
          onClearSearch={handleClearSearch}
        />
        {activeTab === "Storybook" && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="secondary"
                size="sm"
                className="relative p-1.5 h-7 group"
                data-tooltip-id="tooltip"
                data-tooltip-content="Actions"
              >
                <Ellipsis className="w-4 h-4 text-text-secondary group-hover:text-text-primary" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={8}>
              <DropdownMenuItem
                onClick={() => setShowBulkPreviewModal(true)}
                className="flex items-center gap-2 cursor-pointer group"
              >
                <div className="flex items-center gap-1">
                  <PictureInPicture className="w-4 h-4 text-icons-secondary group-hover:text-icons-primary" />
                  <span
                    className="whitespace-nowrap"
                    data-tooltip-id="tooltip"
                    data-tooltip-content="If a storybook has no previews, you can generate them here."
                  >
                    Generate Previews
                  </span>
                  {ungeneratedCount > 0 && (
                    <Badge className="ml-3" variant="primary-sm">
                      {ungeneratedCount > 99 ? "99+" : ungeneratedCount}
                    </Badge>
                  )}
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <div className="overflow-y-auto scrollbar-hide flex-1 relative">
        {isLoading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-primary-0" />
          </div>
        ) : (
          <>
            {activeTab === "Storybook" &&
              (storyComponentsFiltered.length === 0 ? (
                <div className="text-text-primary text-xs p-4">
                  {searchVal
                    ? `0 results for "${searchVal}"`
                    : "No storybook components found."}
                </div>
              ) : (
                <AssetGrid
                  components={storyComponentsFiltered}
                  detailedAsset={detailedAsset}
                  setDetailedAsset={setDetailedAsset}
                  handleAddToCanvas={handleAddToCanvas}
                  getVariants={(component) =>
                    componentKeyToStories[component.key] || []
                  }
                />
              ))}

            {activeTab === "Components" &&
              (componentsFiltered.length === 0 ? (
                <div className="text-text-primary text-xs p-4">
                  {searchVal
                    ? `0 results for "${searchVal}"`
                    : "No components found."}
                </div>
              ) : (
                <AssetGrid
                  components={componentsFiltered}
                  detailedAsset={detailedAsset}
                  setDetailedAsset={setDetailedAsset}
                  handleAddToCanvas={handleAddToCanvas}
                />
              ))}

            {activeTab === "Icons" &&
              (filteredIcons.length === 0 ? (
                <div className="text-text-primary text-xs p-4">
                  {searchVal
                    ? `0 results for "${searchVal}"`
                    : "No icons found."}
                </div>
              ) : (
                <div className="col-span-2">
                  <LucideReactGallery icons={filteredIcons} />
                </div>
              ))}
          </>
        )}
      </div>

      <BulkPreviewModal
        isOpen={showBulkPreviewModal}
        onClose={() => setShowBulkPreviewModal(false)}
      />
    </div>
  );
};
